/**
 * Parking overview (/parking) — V0.3 marquee Tier 1 module.
 *
 * Three-tab surface:
 *   Zones        — CRUD on parking zones (top-level groupings).
 *   Slots        — society-wide slot inventory + filters + bulk import +
 *                  per-row edit / block / unblock / delete.
 *   Allocations  — current + historical allocations (slot → unit). End an
 *                  active allocation with an audit reason.
 *
 * Backend contracts (locked with the parallel API agent):
 *   Zones:
 *     GET    /v1/parking/zones                              → { items, next_cursor? }
 *     POST   /v1/parking/zones                              → ParkingZone
 *     PATCH  /v1/parking/zones/:id
 *     DELETE /v1/parking/zones/:id
 *   Slots:
 *     GET    /v1/parking/slots?zone_id&status&ownership_kind&owner_unit_id&search&cursor&limit
 *     POST   /v1/parking/slots                              → ParkingSlot
 *     PATCH  /v1/parking/slots/:id
 *     DELETE /v1/parking/slots/:id
 *     POST   /v1/parking/slots/bulk                          → bulk import
 *     POST   /v1/parking/slots/bulk-preallocate              → per-unit-type mapping
 *     POST   /v1/parking/slots/:id/block | /unblock
 *   Allocations:
 *     GET    /v1/parking/allocations?slot_id&using_unit_id&allocation_kind&active&cursor&limit
 *     POST   /v1/parking/allocations
 *     PATCH  /v1/parking/allocations/:id
 *     POST   /v1/parking/allocations/:id/end                 → body { end_reason }
 *
 * Visual rules:
 *   - All copy English.
 *   - Tokens only (vt-* prefix).
 *   - `Button` primary variant for primary actions; `ConfirmDialog` for destructive.
 *   - `CursorPaginator` + `useCursorAccumulator` on every list.
 *
 * Pre-allocate dialog: maps a `unit_type` keyword (e.g. 1bhk/2bhk/3bhk) to a
 * count of slots-per-unit. We send the whole `unit_type_mapping` object to the
 * server which allocates accordingly (server is source of truth for the actual
 * slot picking logic — the UI just collects intent).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ParkingCircle,
  Plus,
  Edit3,
  Filter,
  Power,
  RotateCcw,
  Trash2,
  Upload,
  Layers,
  CarFront,
  Eye,
  IndianRupee,
  ShieldOff,
  X,
  ArrowLeftRight,
  Zap,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage, api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate } from '@/lib/format';
import type {
  ParkingZone,
  ParkingSlot,
  ParkingSlotOwnership,
  ParkingSlotStatus,
  ParkingSlotType,
  VehicleSizeClass,
  ParkingAllocation,
  AllocationKind,
  CursorList,
  UnitLite,
  BlockLite
} from './_parking-types';
import {
  SLOT_TYPE_LABEL,
  VEHICLE_SIZE_LABEL,
  OWNERSHIP_LABEL,
  STATUS_LABEL,
  STATUS_BADGE,
  ALLOCATION_KIND_LABEL
} from './_parking-types';

// ───── helpers ─────

function formatInr(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

// ───── form schemas ─────

const ZoneSchema = z.object({
  name: z.string().trim().min(2, 'Name required').max(120),
  code: z.string().trim().min(1, 'Code required').max(20),
  tower_id: z.string().optional().or(z.literal('')),
  total_slots: z.coerce.number().int().min(0).max(10_000),
  default_ownership_kind: z.enum([
    'society_owned',
    'unit_owned',
    'builder_unsold',
    'common_visitor'
  ]),
  default_slot_type: z.enum([
    'covered',
    'open',
    'two_wheeler',
    'ev_charging',
    'handicapped',
    'visitor'
  ]),
  default_vehicle_size: z.enum(['two_wheeler', 'hatchback', 'sedan', 'suv', 'oversized']),
  default_monthly_fee_inr: z.coerce.number().int().min(0).max(1_000_000).optional(),
  notes: z.string().trim().max(1000).optional().or(z.literal('')),
  is_active: z.boolean().default(true)
});
type ZoneForm = z.infer<typeof ZoneSchema>;

const DEFAULT_ZONE_FORM: ZoneForm = {
  name: '',
  code: '',
  tower_id: '',
  total_slots: 0,
  default_ownership_kind: 'society_owned',
  default_slot_type: 'covered',
  default_vehicle_size: 'sedan',
  default_monthly_fee_inr: 0,
  notes: '',
  is_active: true
};

const SlotSchema = z.object({
  zone_id: z.string().min(1, 'Zone required'),
  slot_number: z.string().trim().min(1, 'Slot number required').max(40),
  slot_type: z.enum([
    'covered',
    'open',
    'two_wheeler',
    'ev_charging',
    'handicapped',
    'visitor'
  ]),
  vehicle_size: z.enum(['two_wheeler', 'hatchback', 'sedan', 'suv', 'oversized']),
  ownership_kind: z.enum([
    'society_owned',
    'unit_owned',
    'builder_unsold',
    'common_visitor'
  ]),
  owner_unit_id: z.string().optional().or(z.literal('')),
  purchase_price_inr: z.coerce.number().int().min(0).max(100_000_000).optional(),
  monthly_fee_inr: z.coerce.number().int().min(0).max(1_000_000).optional(),
  reserved_for_special_needs: z.boolean().default(false),
  notes: z.string().trim().max(500).optional().or(z.literal(''))
});
type SlotForm = z.infer<typeof SlotSchema>;

const BulkImportSchema = z
  .object({
    zone_id: z.string().min(1, 'Zone required'),
    prefix: z.string().trim().min(1, 'Prefix required').max(20),
    from_number: z.coerce.number().int().min(1).max(99_999),
    to_number: z.coerce.number().int().min(1).max(99_999),
    slot_type: z.enum([
      'covered',
      'open',
      'two_wheeler',
      'ev_charging',
      'handicapped',
      'visitor'
    ]),
    vehicle_size: z.enum(['two_wheeler', 'hatchback', 'sedan', 'suv', 'oversized']),
    ownership_kind: z.enum([
      'society_owned',
      'unit_owned',
      'builder_unsold',
      'common_visitor'
    ])
  })
  .refine((v) => v.to_number >= v.from_number, {
    message: 'To-number must be ≥ from-number',
    path: ['to_number']
  });
type BulkImportForm = z.infer<typeof BulkImportSchema>;

// Pre-allocate maps a `unit_type` keyword → slots-per-unit count. We collect
// up to 4 rows in the UI then send the assembled object to the server.
interface PreallocateRow {
  unit_type: string;
  slots_per_unit: number;
}

const AllocationCreateSchema = z
  .object({
    slot_id: z.string().min(1, 'Slot required'),
    using_unit_id: z.string().min(1, 'Unit required'),
    allocation_kind: z.enum([
      'owner_self_use',
      'society_rental',
      'owner_rental',
      'guest_temp'
    ]),
    monthly_rent_inr: z.coerce.number().int().min(0).max(1_000_000).optional(),
    society_cut_pct: z.coerce.number().int().min(0).max(100).optional(),
    rental_start: z.string().optional().or(z.literal('')),
    rental_end: z.string().optional().or(z.literal('')),
    is_primary_slot: z.boolean().default(false),
    notes: z.string().trim().max(500).optional().or(z.literal(''))
  })
  .refine(
    (v) =>
      v.allocation_kind === 'owner_self_use' ||
      v.allocation_kind === 'guest_temp' ||
      (v.monthly_rent_inr !== undefined && v.monthly_rent_inr >= 0),
    { message: 'Rent required for rental allocations', path: ['monthly_rent_inr'] }
  );
type AllocationCreateForm = z.infer<typeof AllocationCreateSchema>;

const EndAllocationSchema = z.object({
  end_reason: z.string().trim().min(3, 'Reason required').max(500)
});
type EndAllocationForm = z.infer<typeof EndAllocationSchema>;

// ───── component ─────

type Tab = 'zones' | 'slots' | 'allocations';

export function ParkingPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('zones');

  // ─── Zones list (single page; usually small)
  const zonesQuery = useQuery({
    queryKey: ['parking-zones'],
    queryFn: () => get<CursorList<ParkingZone>>('/parking/zones', { params: { limit: 200 } })
  });
  const zones = zonesQuery.data?.items ?? [];

  // ─── Blocks list (for zone form's tower_id picker)
  const blocksQuery = useQuery({
    queryKey: ['society-blocks'],
    queryFn: () => get<{ items: BlockLite[] }>('/society/blocks'),
    staleTime: 5 * 60 * 1000
  });
  const blocks = blocksQuery.data?.items ?? [];

  // ─── Slot filters
  const [slotZoneId, setSlotZoneId] = useState<string>('');
  const [slotStatusFilter, setSlotStatusFilter] = useState<string>('');
  const [slotOwnershipFilter, setSlotOwnershipFilter] = useState<string>('');
  const [slotSearch, setSlotSearch] = useState<string>('');

  const slotFiltersHash = useMemo(
    () => JSON.stringify([slotZoneId, slotStatusFilter, slotOwnershipFilter, slotSearch.trim()]),
    [slotZoneId, slotStatusFilter, slotOwnershipFilter, slotSearch]
  );
  const [slotCursor, setSlotCursor] = useState<string | null>(null);
  useEffect(() => {
    setSlotCursor(null);
  }, [slotFiltersHash]);

  const slotsQuery = useQuery({
    queryKey: ['parking-slots', slotZoneId, slotStatusFilter, slotOwnershipFilter, slotSearch, slotCursor],
    queryFn: () =>
      get<CursorList<ParkingSlot>>('/parking/slots', {
        params: {
          limit: 50,
          ...(slotZoneId ? { zone_id: slotZoneId } : {}),
          ...(slotStatusFilter ? { status: slotStatusFilter } : {}),
          ...(slotOwnershipFilter ? { ownership_kind: slotOwnershipFilter } : {}),
          ...(slotSearch.trim() ? { search: slotSearch.trim() } : {}),
          ...(slotCursor ? { cursor: slotCursor } : {})
        }
      }),
    enabled: tab === 'slots' || tab === 'zones' // zones tab uses count totals
  });
  const slotPaginator = useCursorAccumulator<ParkingSlot>(slotsQuery.data, slotCursor, slotFiltersHash);
  const slots = slotPaginator.items;

  // ─── Allocation filters
  const [allocActiveOnly, setAllocActiveOnly] = useState<'active' | 'history' | 'all'>('active');
  const [allocKindFilter, setAllocKindFilter] = useState<string>('');
  const allocFiltersHash = useMemo(
    () => JSON.stringify([allocActiveOnly, allocKindFilter]),
    [allocActiveOnly, allocKindFilter]
  );
  const [allocCursor, setAllocCursor] = useState<string | null>(null);
  useEffect(() => {
    setAllocCursor(null);
  }, [allocFiltersHash]);

  const allocsQuery = useQuery({
    queryKey: ['parking-allocations', allocActiveOnly, allocKindFilter, allocCursor],
    queryFn: () =>
      get<CursorList<ParkingAllocation>>('/parking/allocations', {
        params: {
          limit: 50,
          ...(allocActiveOnly === 'active' ? { active: true } : {}),
          ...(allocActiveOnly === 'history' ? { active: false } : {}),
          ...(allocKindFilter ? { allocation_kind: allocKindFilter } : {}),
          ...(allocCursor ? { cursor: allocCursor } : {})
        }
      }),
    enabled: tab === 'allocations'
  });
  const allocPaginator = useCursorAccumulator<ParkingAllocation>(
    allocsQuery.data,
    allocCursor,
    allocFiltersHash
  );
  const allocations = allocPaginator.items;

  // ─── Chargers count (for KPI strip — keep server pressure minimal)
  const chargersCountQuery = useQuery({
    queryKey: ['parking-chargers', 'kpi-count'],
    queryFn: () => get<{ items: unknown[] }>('/parking/chargers', { params: { limit: 1 } }),
    staleTime: 60_000
  });

  // ─── KPI strip computed from accumulated rows (no extra summary endpoint).
  const totalZones = zones.length;
  const totalSlots = useMemo(() => zones.reduce((s, z) => s + (z.total_slots ?? 0), 0), [zones]);
  const occupiedCount = useMemo(
    () => slots.filter((s) => s.status === 'allocated').length,
    [slots]
  );
  const availableCount = useMemo(
    () => slots.filter((s) => s.status === 'available').length,
    [slots]
  );
  const blockedCount = useMemo(
    () => slots.filter((s) => s.status === 'blocked').length,
    [slots]
  );
  const monthlyRevenueEstimate = useMemo(
    () => slots.reduce((s, sl) => s + (sl.monthly_fee_inr ?? 0), 0),
    [slots]
  );

  // ─── Zone create/edit form
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<ParkingZone | null>(null);
  const zoneForm = useForm<ZoneForm>({
    resolver: zodResolver(ZoneSchema),
    defaultValues: DEFAULT_ZONE_FORM,
    mode: 'onChange'
  });
  const openCreateZone = (): void => {
    setEditingZone(null);
    zoneForm.reset(DEFAULT_ZONE_FORM);
    setZoneDialogOpen(true);
  };
  const openEditZone = (z: ParkingZone): void => {
    setEditingZone(z);
    zoneForm.reset({
      name: z.name,
      code: z.code,
      tower_id: z.tower_id ?? '',
      total_slots: z.total_slots,
      default_ownership_kind: z.default_ownership_kind,
      default_slot_type: z.default_slot_type,
      default_vehicle_size: z.default_vehicle_size,
      default_monthly_fee_inr: z.default_monthly_fee_inr ?? 0,
      notes: z.notes ?? '',
      is_active: z.is_active
    });
    setZoneDialogOpen(true);
  };

  const saveZone = useMutation({
    mutationFn: (v: ZoneForm) => {
      const body = {
        name: v.name,
        code: v.code,
        tower_id: v.tower_id?.trim() || null,
        total_slots: v.total_slots,
        default_ownership_kind: v.default_ownership_kind,
        default_slot_type: v.default_slot_type,
        default_vehicle_size: v.default_vehicle_size,
        default_monthly_fee_inr: v.default_monthly_fee_inr ?? null,
        notes: v.notes?.trim() || null,
        is_active: v.is_active
      };
      if (editingZone) return patch<ParkingZone>(`/parking/zones/${editingZone.id}`, body);
      return post<ParkingZone>('/parking/zones', body);
    },
    onSuccess: () => {
      toast.success(editingZone ? 'Zone updated' : 'Zone created');
      setZoneDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['parking-zones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const [deleteZoneTarget, setDeleteZoneTarget] = useState<ParkingZone | null>(null);
  const deleteZone = useMutation({
    mutationFn: (id: string) => del<unknown>(`/parking/zones/${id}`),
    onSuccess: () => {
      toast.success('Zone deleted');
      setDeleteZoneTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-zones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── Slot create/edit dialog
  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<ParkingSlot | null>(null);
  const slotForm = useForm<SlotForm>({
    resolver: zodResolver(SlotSchema),
    mode: 'onChange',
    defaultValues: {
      zone_id: '',
      slot_number: '',
      slot_type: 'covered',
      vehicle_size: 'sedan',
      ownership_kind: 'society_owned',
      owner_unit_id: '',
      purchase_price_inr: 0,
      monthly_fee_inr: 0,
      reserved_for_special_needs: false,
      notes: ''
    }
  });
  const slotOwnership = slotForm.watch('ownership_kind') as ParkingSlotOwnership;

  // ─── Units lookup for owner-unit picker (only loaded when slot dialog opens
  // AND ownership = unit_owned — keeps the page light on first paint).
  const unitsQuery = useQuery({
    queryKey: ['units-lite'],
    queryFn: () => get<{ items: UnitLite[] }>('/society/units', { params: { limit: 1000 } }),
    enabled: slotDialogOpen && slotOwnership === 'unit_owned',
    staleTime: 5 * 60 * 1000
  });
  const units = unitsQuery.data?.items ?? [];

  const openCreateSlot = (zoneId?: string): void => {
    setEditingSlot(null);
    const z = zoneId ? zones.find((zz) => zz.id === zoneId) : null;
    slotForm.reset({
      zone_id: zoneId ?? '',
      slot_number: '',
      slot_type: z?.default_slot_type ?? 'covered',
      vehicle_size: z?.default_vehicle_size ?? 'sedan',
      ownership_kind: z?.default_ownership_kind ?? 'society_owned',
      owner_unit_id: '',
      purchase_price_inr: 0,
      monthly_fee_inr: z?.default_monthly_fee_inr ?? 0,
      reserved_for_special_needs: false,
      notes: ''
    });
    setSlotDialogOpen(true);
  };
  const openEditSlot = (s: ParkingSlot): void => {
    setEditingSlot(s);
    slotForm.reset({
      zone_id: s.zone_id,
      slot_number: s.slot_number,
      slot_type: s.slot_type,
      vehicle_size: s.vehicle_size,
      ownership_kind: s.ownership_kind,
      owner_unit_id: s.owner_unit_id ?? '',
      purchase_price_inr: s.purchase_price_inr ?? 0,
      monthly_fee_inr: s.monthly_fee_inr ?? 0,
      reserved_for_special_needs: s.reserved_for_special_needs,
      notes: s.notes ?? ''
    });
    setSlotDialogOpen(true);
  };

  const saveSlot = useMutation({
    mutationFn: (v: SlotForm) => {
      const body: Record<string, unknown> = {
        zone_id: v.zone_id,
        slot_number: v.slot_number,
        slot_type: v.slot_type,
        vehicle_size: v.vehicle_size,
        ownership_kind: v.ownership_kind,
        owner_unit_id: v.ownership_kind === 'unit_owned' ? v.owner_unit_id || null : null,
        purchase_price_inr:
          v.ownership_kind === 'unit_owned' ? v.purchase_price_inr ?? null : null,
        monthly_fee_inr: v.monthly_fee_inr ?? null,
        reserved_for_special_needs: v.reserved_for_special_needs,
        notes: v.notes?.trim() || null
      };
      if (editingSlot) return patch<ParkingSlot>(`/parking/slots/${editingSlot.id}`, body);
      return post<ParkingSlot>('/parking/slots', body);
    },
    onSuccess: () => {
      toast.success(editingSlot ? 'Slot updated' : 'Slot created');
      setSlotDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-zones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Block / unblock slot
  const blockSlot = useMutation({
    mutationFn: (id: string) => post<ParkingSlot>(`/parking/slots/${id}/block`, {}),
    onSuccess: () => {
      toast.success('Slot blocked');
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const unblockSlot = useMutation({
    mutationFn: (id: string) => post<ParkingSlot>(`/parking/slots/${id}/unblock`, {}),
    onSuccess: () => {
      toast.success('Slot unblocked');
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const [deleteSlotTarget, setDeleteSlotTarget] = useState<ParkingSlot | null>(null);
  const deleteSlot = useMutation({
    mutationFn: (id: string) => del<unknown>(`/parking/slots/${id}`),
    onSuccess: () => {
      toast.success('Slot deleted');
      setDeleteSlotTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-zones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── Sale-deed upload (post slot save).  Inline in the edit row "..." menu
  // would clutter — keep it on the per-row Eye → zone detail. For the list page
  // we expose it only via the "Edit slot" Dialog if ownership = unit_owned.
  const uploadDeed = useMutation({
    mutationFn: async ({ slotId, file }: { slotId: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/parking/slots/${slotId}/sale-deed`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Sale deed uploaded');
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── Bulk import dialog
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const bulkForm = useForm<BulkImportForm>({
    resolver: zodResolver(BulkImportSchema),
    mode: 'onChange',
    defaultValues: {
      zone_id: '',
      prefix: '',
      from_number: 1,
      to_number: 20,
      slot_type: 'covered',
      vehicle_size: 'sedan',
      ownership_kind: 'society_owned'
    }
  });
  const bulkImport = useMutation({
    mutationFn: (v: BulkImportForm) =>
      post<{ created: number }>('/parking/slots/bulk', {
        zone_id: v.zone_id,
        prefix: v.prefix,
        from_number: v.from_number,
        to_number: v.to_number,
        slot_type: v.slot_type,
        vehicle_size: v.vehicle_size,
        ownership_kind: v.ownership_kind
      }),
    onSuccess: (data) => {
      toast.success(`Created ${data.created} slot${data.created === 1 ? '' : 's'}`);
      setBulkDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-zones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── Pre-allocate dialog
  const [preallocDialogOpen, setPreallocDialogOpen] = useState(false);
  const [preallocRows, setPreallocRows] = useState<PreallocateRow[]>([
    { unit_type: '1bhk', slots_per_unit: 1 },
    { unit_type: '2bhk', slots_per_unit: 1 },
    { unit_type: '3bhk', slots_per_unit: 2 }
  ]);
  const preallocateSlots = useMutation({
    mutationFn: (rows: PreallocateRow[]) => {
      const unit_type_mapping: Record<string, number> = {};
      for (const r of rows) {
        if (r.unit_type.trim()) unit_type_mapping[r.unit_type.trim()] = r.slots_per_unit;
      }
      return post<{ allocated: number }>('/parking/slots/bulk-preallocate', {
        unit_type_mapping
      });
    },
    onSuccess: (data) => {
      toast.success(`Pre-allocated ${data.allocated} slot${data.allocated === 1 ? '' : 's'}`);
      setPreallocDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── Allocation create dialog
  const [allocDialogOpen, setAllocDialogOpen] = useState(false);
  const allocForm = useForm<AllocationCreateForm>({
    resolver: zodResolver(AllocationCreateSchema),
    mode: 'onChange',
    defaultValues: {
      slot_id: '',
      using_unit_id: '',
      allocation_kind: 'owner_self_use',
      monthly_rent_inr: 0,
      society_cut_pct: 0,
      rental_start: '',
      rental_end: '',
      is_primary_slot: false,
      notes: ''
    }
  });
  const allocKind = allocForm.watch('allocation_kind') as AllocationKind;
  const isRentalKind = allocKind === 'society_rental' || allocKind === 'owner_rental';
  // Slots picker — available slots only (limit 200 — covers most societies).
  const availableSlotsQuery = useQuery({
    queryKey: ['parking-slots', 'available-picker'],
    queryFn: () =>
      get<CursorList<ParkingSlot>>('/parking/slots', { params: { status: 'available', limit: 200 } }),
    enabled: allocDialogOpen,
    staleTime: 30_000
  });
  // Units picker — same units list as slot form.
  const unitsForAllocQ = useQuery({
    queryKey: ['units-lite'],
    queryFn: () => get<{ items: UnitLite[] }>('/society/units', { params: { limit: 1000 } }),
    enabled: allocDialogOpen,
    staleTime: 5 * 60 * 1000
  });

  const createAllocation = useMutation({
    mutationFn: (v: AllocationCreateForm) =>
      post<ParkingAllocation>('/parking/allocations', {
        slot_id: v.slot_id,
        using_unit_id: v.using_unit_id,
        allocation_kind: v.allocation_kind,
        monthly_rent_inr: isRentalKind ? v.monthly_rent_inr ?? 0 : null,
        society_cut_pct: isRentalKind ? v.society_cut_pct ?? 0 : null,
        rental_start: v.rental_start || null,
        rental_end: v.rental_end || null,
        is_primary_slot: v.is_primary_slot,
        notes: v.notes?.trim() || null
      }),
    onSuccess: () => {
      toast.success('Allocation created');
      setAllocDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['parking-allocations'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── End allocation dialog
  const [endAllocTarget, setEndAllocTarget] = useState<ParkingAllocation | null>(null);
  const endAllocForm = useForm<EndAllocationForm>({
    resolver: zodResolver(EndAllocationSchema),
    mode: 'onChange',
    defaultValues: { end_reason: '' }
  });
  useEffect(() => {
    if (endAllocTarget) endAllocForm.reset({ end_reason: '' });
  }, [endAllocTarget]); // eslint-disable-line react-hooks/exhaustive-deps
  const endAllocation = useMutation({
    mutationFn: (v: EndAllocationForm) => {
      if (!endAllocTarget) throw new Error('No allocation');
      return post<ParkingAllocation>(`/parking/allocations/${endAllocTarget.id}/end`, {
        end_reason: v.end_reason
      });
    },
    onSuccess: () => {
      toast.success('Allocation ended');
      setEndAllocTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-allocations'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── render ─────

  const zoneDialog = (
    <Dialog open={zoneDialogOpen} onOpenChange={setZoneDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingZone ? 'Edit zone' : 'Add parking zone'}</DialogTitle>
          <DialogDescription>
            A zone groups parking slots — typically by basement / level / tower. Defaults below
            seed new slots created inside this zone.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={zoneForm.handleSubmit((v) => saveZone.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="z-name">Name</Label>
              <Input id="z-name" placeholder="Basement 1" {...zoneForm.register('name')} />
              {zoneForm.formState.errors.name ? (
                <p className="text-vt-12 text-vt-alert">{zoneForm.formState.errors.name.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="z-code">Code</Label>
              <Input id="z-code" placeholder="B1" {...zoneForm.register('code')} />
              {zoneForm.formState.errors.code ? (
                <p className="text-vt-12 text-vt-alert">{zoneForm.formState.errors.code.message}</p>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="z-tower">Tower (optional)</Label>
              <Select id="z-tower" {...zoneForm.register('tower_id')}>
                <option value="">— Society-wide —</option>
                {blocks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="z-total">Total slots</Label>
              <Input
                id="z-total"
                type="number"
                min={0}
                {...zoneForm.register('total_slots')}
              />
            </div>
          </div>
          <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Defaults for new slots
            </legend>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="z-own">Ownership</Label>
                <Select id="z-own" {...zoneForm.register('default_ownership_kind')}>
                  {(Object.keys(OWNERSHIP_LABEL) as ParkingSlotOwnership[]).map((k) => (
                    <option key={k} value={k}>
                      {OWNERSHIP_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="z-type">Slot type</Label>
                <Select id="z-type" {...zoneForm.register('default_slot_type')}>
                  {(Object.keys(SLOT_TYPE_LABEL) as ParkingSlotType[]).map((k) => (
                    <option key={k} value={k}>
                      {SLOT_TYPE_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="z-size">Vehicle size</Label>
                <Select id="z-size" {...zoneForm.register('default_vehicle_size')}>
                  {(Object.keys(VEHICLE_SIZE_LABEL) as VehicleSizeClass[]).map((k) => (
                    <option key={k} value={k}>
                      {VEHICLE_SIZE_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="z-fee">Default monthly fee (₹)</Label>
              <Input
                id="z-fee"
                type="number"
                min={0}
                {...zoneForm.register('default_monthly_fee_inr')}
              />
            </div>
          </fieldset>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="z-notes">Notes</Label>
            <Textarea id="z-notes" rows={2} {...zoneForm.register('notes')} />
          </div>
          <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
            <input
              type="checkbox"
              {...zoneForm.register('is_active')}
              className="h-4 w-4 accent-vt-primary"
            />
            Active
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setZoneDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saveZone.isPending}
              loadingText="Saving…"
              disabled={saveZone.isPending || !zoneForm.formState.isValid}
            >
              {editingZone ? 'Save changes' : 'Create zone'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const slotDialog = (
    <Dialog open={slotDialogOpen} onOpenChange={setSlotDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingSlot ? 'Edit slot' : 'Add parking slot'}</DialogTitle>
          <DialogDescription>
            {editingSlot
              ? 'Flipping ownership writes an audit entry — make sure the sale deed reference is current.'
              : 'Add a single slot. For bulk runs use Bulk import.'}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={slotForm.handleSubmit((v) => saveSlot.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-zone">Zone</Label>
              <Select id="s-zone" {...slotForm.register('zone_id')}>
                <option value="">— Pick zone —</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name} ({z.code})
                  </option>
                ))}
              </Select>
              {slotForm.formState.errors.zone_id ? (
                <p className="text-vt-12 text-vt-alert">{slotForm.formState.errors.zone_id.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-num">Slot number</Label>
              <Input id="s-num" placeholder="B1-014" {...slotForm.register('slot_number')} />
              {slotForm.formState.errors.slot_number ? (
                <p className="text-vt-12 text-vt-alert">{slotForm.formState.errors.slot_number.message}</p>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-type">Type</Label>
              <Select id="s-type" {...slotForm.register('slot_type')}>
                {(Object.keys(SLOT_TYPE_LABEL) as ParkingSlotType[]).map((k) => (
                  <option key={k} value={k}>
                    {SLOT_TYPE_LABEL[k]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-size">Vehicle size</Label>
              <Select id="s-size" {...slotForm.register('vehicle_size')}>
                {(Object.keys(VEHICLE_SIZE_LABEL) as VehicleSizeClass[]).map((k) => (
                  <option key={k} value={k}>
                    {VEHICLE_SIZE_LABEL[k]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-own">Ownership</Label>
              <Select id="s-own" {...slotForm.register('ownership_kind')}>
                {(Object.keys(OWNERSHIP_LABEL) as ParkingSlotOwnership[]).map((k) => (
                  <option key={k} value={k}>
                    {OWNERSHIP_LABEL[k]}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          {slotOwnership === 'unit_owned' ? (
            <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
              <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                Owner-unit details
              </legend>
              <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="s-unit">Owner unit</Label>
                  <Select id="s-unit" {...slotForm.register('owner_unit_id')}>
                    <option value="">— Pick unit —</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.block ? `${u.block.name} · ` : ''}
                        {u.number}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="s-price">Purchase price (₹)</Label>
                  <Input
                    id="s-price"
                    type="number"
                    min={0}
                    {...slotForm.register('purchase_price_inr')}
                  />
                </div>
              </div>
              {editingSlot ? (
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="s-deed">Sale deed (PDF / image)</Label>
                  <Input
                    id="s-deed"
                    type="file"
                    accept="application/pdf,image/png,image/jpeg"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f && editingSlot) uploadDeed.mutate({ slotId: editingSlot.id, file: f });
                      e.target.value = '';
                    }}
                  />
                  {editingSlot.sale_deed_ref ? (
                    <p className="text-vt-12 text-vt-text-muted">
                      On file: {editingSlot.sale_deed_ref}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </fieldset>
          ) : null}
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-fee">Monthly fee (₹)</Label>
              <Input
                id="s-fee"
                type="number"
                min={0}
                {...slotForm.register('monthly_fee_inr')}
              />
            </div>
            <label className="flex items-end gap-vt-2 pb-vt-2 text-vt-13 text-vt-text">
              <input
                type="checkbox"
                {...slotForm.register('reserved_for_special_needs')}
                className="h-4 w-4 accent-vt-primary"
              />
              Reserved for special-needs (handicapped)
            </label>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="s-notes">Notes</Label>
            <Textarea id="s-notes" rows={2} {...slotForm.register('notes')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setSlotDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saveSlot.isPending}
              loadingText="Saving…"
              disabled={saveSlot.isPending || !slotForm.formState.isValid}
            >
              {editingSlot ? 'Save changes' : 'Create slot'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const bulkDialog = (
    <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk import slots</DialogTitle>
          <DialogDescription>
            Generates slots <span className="font-mono">PREFIX-N</span> for each N in [from, to].
            Existing slot numbers in the zone are skipped server-side.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={bulkForm.handleSubmit((v) => bulkImport.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="b-zone">Zone</Label>
            <Select id="b-zone" {...bulkForm.register('zone_id')}>
              <option value="">— Pick zone —</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name} ({z.code})
                </option>
              ))}
            </Select>
            {bulkForm.formState.errors.zone_id ? (
              <p className="text-vt-12 text-vt-alert">{bulkForm.formState.errors.zone_id.message}</p>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="b-prefix">Prefix</Label>
              <Input id="b-prefix" placeholder="B1-" {...bulkForm.register('prefix')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="b-from">From</Label>
              <Input id="b-from" type="number" min={1} {...bulkForm.register('from_number')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="b-to">To</Label>
              <Input id="b-to" type="number" min={1} {...bulkForm.register('to_number')} />
              {bulkForm.formState.errors.to_number ? (
                <p className="text-vt-12 text-vt-alert">{bulkForm.formState.errors.to_number.message}</p>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="b-type">Type</Label>
              <Select id="b-type" {...bulkForm.register('slot_type')}>
                {(Object.keys(SLOT_TYPE_LABEL) as ParkingSlotType[]).map((k) => (
                  <option key={k} value={k}>
                    {SLOT_TYPE_LABEL[k]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="b-size">Vehicle size</Label>
              <Select id="b-size" {...bulkForm.register('vehicle_size')}>
                {(Object.keys(VEHICLE_SIZE_LABEL) as VehicleSizeClass[]).map((k) => (
                  <option key={k} value={k}>
                    {VEHICLE_SIZE_LABEL[k]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="b-own">Ownership</Label>
              <Select id="b-own" {...bulkForm.register('ownership_kind')}>
                {(Object.keys(OWNERSHIP_LABEL) as ParkingSlotOwnership[]).map((k) => (
                  <option key={k} value={k}>
                    {OWNERSHIP_LABEL[k]}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setBulkDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={bulkImport.isPending}
              loadingText="Importing…"
              disabled={bulkImport.isPending || !bulkForm.formState.isValid}
            >
              <Upload className="h-4 w-4" /> Import
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const preallocDialog = (
    <Dialog open={preallocDialogOpen} onOpenChange={setPreallocDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pre-allocate slots by unit type</DialogTitle>
          <DialogDescription>
            Assign available society-owned slots to units based on their unit-type. Add rows for
            every type (1bhk, 2bhk, …) and the count of slots each unit of that type gets.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-vt-3">
          {preallocRows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_120px_40px] items-end gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor={`pa-type-${idx}`}>Unit type</Label>
                <Input
                  id={`pa-type-${idx}`}
                  placeholder="2bhk"
                  value={row.unit_type}
                  onChange={(e) => {
                    const next = [...preallocRows];
                    next[idx] = { ...next[idx], unit_type: e.target.value };
                    setPreallocRows(next);
                  }}
                />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor={`pa-cnt-${idx}`}>Slots / unit</Label>
                <Input
                  id={`pa-cnt-${idx}`}
                  type="number"
                  min={0}
                  value={row.slots_per_unit}
                  onChange={(e) => {
                    const next = [...preallocRows];
                    next[idx] = { ...next[idx], slots_per_unit: parseInt(e.target.value || '0', 10) };
                    setPreallocRows(next);
                  }}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                title="Remove"
                onClick={() => setPreallocRows(preallocRows.filter((_, i) => i !== idx))}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setPreallocRows([...preallocRows, { unit_type: '', slots_per_unit: 1 }])}
          >
            <Plus className="h-4 w-4" /> Add row
          </Button>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setPreallocDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => preallocateSlots.mutate(preallocRows)}
            loading={preallocateSlots.isPending}
            loadingText="Allocating…"
            disabled={preallocateSlots.isPending}
          >
            Run pre-allocation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const allocDialog = (
    <Dialog open={allocDialogOpen} onOpenChange={setAllocDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New allocation</DialogTitle>
          <DialogDescription>
            Bind an available slot to a unit. Rental kinds also require monthly rent + society cut.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={allocForm.handleSubmit((v) => createAllocation.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-slot">Slot</Label>
              <Select id="a-slot" {...allocForm.register('slot_id')}>
                <option value="">— Pick available slot —</option>
                {(availableSlotsQuery.data?.items ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.zone?.code ? `${s.zone.code} · ` : ''}
                    {s.slot_number} ({SLOT_TYPE_LABEL[s.slot_type]})
                  </option>
                ))}
              </Select>
              {allocForm.formState.errors.slot_id ? (
                <p className="text-vt-12 text-vt-alert">{allocForm.formState.errors.slot_id.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-unit">Using unit</Label>
              <Select id="a-unit" {...allocForm.register('using_unit_id')}>
                <option value="">— Pick unit —</option>
                {(unitsForAllocQ.data?.items ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.block ? `${u.block.name} · ` : ''}
                    {u.number}
                  </option>
                ))}
              </Select>
              {allocForm.formState.errors.using_unit_id ? (
                <p className="text-vt-12 text-vt-alert">{allocForm.formState.errors.using_unit_id.message}</p>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="a-kind">Allocation kind</Label>
            <Select id="a-kind" {...allocForm.register('allocation_kind')}>
              {(Object.keys(ALLOCATION_KIND_LABEL) as AllocationKind[]).map((k) => (
                <option key={k} value={k}>
                  {ALLOCATION_KIND_LABEL[k]}
                </option>
              ))}
            </Select>
          </div>
          {isRentalKind ? (
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="a-rent">Monthly rent (₹)</Label>
                <Input
                  id="a-rent"
                  type="number"
                  min={0}
                  {...allocForm.register('monthly_rent_inr')}
                />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="a-cut">Society cut (%)</Label>
                <Input
                  id="a-cut"
                  type="number"
                  min={0}
                  max={100}
                  {...allocForm.register('society_cut_pct')}
                />
              </div>
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-start">Start</Label>
              <Input id="a-start" type="date" {...allocForm.register('rental_start')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-end">End (optional)</Label>
              <Input id="a-end" type="date" {...allocForm.register('rental_end')} />
            </div>
          </div>
          <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
            <input
              type="checkbox"
              {...allocForm.register('is_primary_slot')}
              className="h-4 w-4 accent-vt-primary"
            />
            Mark as this unit's primary slot
          </label>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="a-notes">Notes</Label>
            <Textarea id="a-notes" rows={2} {...allocForm.register('notes')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setAllocDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createAllocation.isPending}
              loadingText="Allocating…"
              disabled={createAllocation.isPending || !allocForm.formState.isValid}
            >
              Allocate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const endAllocDialog = (
    <Dialog
      open={Boolean(endAllocTarget)}
      onOpenChange={(v) => (v ? null : setEndAllocTarget(null))}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>End allocation</DialogTitle>
          <DialogDescription>
            {endAllocTarget?.slot
              ? `Slot ${endAllocTarget.slot.slot_number} · ${ALLOCATION_KIND_LABEL[endAllocTarget.allocation_kind]}`
              : 'This frees the slot back to the available pool.'}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={endAllocForm.handleSubmit((v) => endAllocation.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="ea-reason">Reason</Label>
            <Textarea
              id="ea-reason"
              rows={3}
              placeholder="e.g. Resident vacating; rental period over."
              {...endAllocForm.register('end_reason')}
            />
            {endAllocForm.formState.errors.end_reason ? (
              <p className="text-vt-12 text-vt-alert">{endAllocForm.formState.errors.end_reason.message}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setEndAllocTarget(null)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              loading={endAllocation.isPending}
              loadingText="Ending…"
              disabled={endAllocation.isPending || !endAllocForm.formState.isValid}
            >
              End allocation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const chargersTotal = chargersCountQuery.data?.items.length ?? 0;

  return (
    <>
      <PageHeader
        title="Parking"
        description="Zones, slots, allocations, and revenue — full parking inventory at a glance."
        actions={
          <div className="flex items-center gap-vt-2">
            <Button size="sm" variant="secondary" onClick={() => navigate({ to: '/parking/disputes' })}>
              <AlertTriangle className="h-4 w-4" /> Disputes
            </Button>
            <Button size="sm" variant="secondary" onClick={() => navigate({ to: '/parking/marketplace' })}>
              <ArrowLeftRight className="h-4 w-4" /> Marketplace
            </Button>
            <Button size="sm" variant="secondary" onClick={() => navigate({ to: '/parking/chargers' })}>
              <Zap className="h-4 w-4" /> EV chargers
            </Button>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-vt-4 md:grid-cols-3 xl:grid-cols-7">
          <KpiCard icon={Layers} label="Zones" value={totalZones} loading={zonesQuery.isLoading} tone="primary" />
          <KpiCard icon={ParkingCircle} label="Total slots" value={totalSlots} loading={zonesQuery.isLoading} tone="primary" />
          <KpiCard icon={CarFront} label="Occupied" value={occupiedCount} loading={slotsQuery.isLoading} tone="info" />
          <KpiCard icon={ParkingCircle} label="Available" value={availableCount} loading={slotsQuery.isLoading} tone="safe" />
          <KpiCard icon={ShieldOff} label="Blocked" value={blockedCount} loading={slotsQuery.isLoading} tone="muted" />
          <KpiCard icon={Zap} label="EV chargers" value={chargersTotal} loading={chargersCountQuery.isLoading} tone="warn" />
          <KpiCard
            icon={IndianRupee}
            label="Monthly est. (loaded)"
            value={formatInr(monthlyRevenueEstimate)}
            loading={slotsQuery.isLoading}
            tone="warn"
            isText
          />
        </div>

        {/* Tabs */}
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as Tab)}
              options={[
                { value: 'zones', label: 'Zones', count: zones.length || undefined },
                { value: 'slots', label: 'Slots' },
                { value: 'allocations', label: 'Allocations' }
              ]}
              aria-label="Parking sections"
            />
          </CardContent>
        </Card>

        {/* Zones tab */}
        {tab === 'zones' ? (
          <Card>
            <CardContent className="p-vt-4">
              <div className="mb-vt-3 flex items-center justify-between">
                <div className="text-vt-13 text-vt-text-muted">
                  Group slots by basement, level, or tower.
                </div>
                <Button size="sm" onClick={openCreateZone}>
                  <Plus className="h-4 w-4" /> Add zone
                </Button>
              </div>
              {zonesQuery.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : zonesQuery.isError ? (
                <EmptyState
                  icon={<Layers className="h-6 w-6" />}
                  title="Could not load zones"
                  description={getErrorMessage(zonesQuery.error)}
                  action={
                    <Button variant="secondary" onClick={() => void zonesQuery.refetch()}>
                      Try again
                    </Button>
                  }
                />
              ) : zones.length === 0 ? (
                <EmptyState
                  icon={<Layers className="h-6 w-6" />}
                  title="No zones yet"
                  description="Create the first parking zone to start adding slots."
                  action={
                    <Button onClick={openCreateZone}>
                      <Plus className="h-4 w-4" /> Add zone
                    </Button>
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Default type</TableHead>
                      <TableHead>Default ownership</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {zones.map((z) => (
                      <TableRow
                        key={z.id}
                        className="cursor-pointer hover:bg-vt-surface-2"
                        onClick={() => navigate({ to: '/parking/zones/$id', params: { id: z.id } })}
                      >
                        <TableCell className="font-medium text-vt-text">{z.name}</TableCell>
                        <TableCell className="font-mono text-vt-13 text-vt-text-muted">
                          {z.code}
                        </TableCell>
                        <TableCell className="text-right text-vt-text-muted">
                          {z.total_slots}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {SLOT_TYPE_LABEL[z.default_slot_type]}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {OWNERSHIP_LABEL[z.default_ownership_kind]}
                        </TableCell>
                        <TableCell>
                          {z.is_active ? (
                            <Badge variant="ok">Active</Badge>
                          ) : (
                            <Badge variant="muted">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-vt-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              title="View slots"
                              onClick={() => navigate({ to: '/parking/zones/$id', params: { id: z.id } })}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Edit zone"
                              onClick={() => openEditZone(z)}
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Delete"
                              onClick={() => setDeleteZoneTarget(z)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* Slots tab */}
        {tab === 'slots' ? (
          <Card>
            <CardContent className="p-vt-4">
              <div className="mb-vt-3 flex flex-wrap items-center gap-vt-3">
                <Filter className="h-4 w-4 text-vt-text-muted" />
                <div className="flex-1 min-w-[180px]">
                  <Select value={slotZoneId} onChange={(e) => setSlotZoneId(e.target.value)}>
                    <option value="">All zones</option>
                    {zones.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 min-w-[140px]">
                  <Select
                    value={slotStatusFilter}
                    onChange={(e) => setSlotStatusFilter(e.target.value)}
                  >
                    <option value="">All statuses</option>
                    {(Object.keys(STATUS_LABEL) as ParkingSlotStatus[]).map((k) => (
                      <option key={k} value={k}>
                        {STATUS_LABEL[k]}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <Select
                    value={slotOwnershipFilter}
                    onChange={(e) => setSlotOwnershipFilter(e.target.value)}
                  >
                    <option value="">All ownership</option>
                    {(Object.keys(OWNERSHIP_LABEL) as ParkingSlotOwnership[]).map((k) => (
                      <option key={k} value={k}>
                        {OWNERSHIP_LABEL[k]}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <Input
                    placeholder="Search slot # or notes…"
                    className="h-10 text-vt-13"
                    value={slotSearch}
                    onChange={(e) => setSlotSearch(e.target.value)}
                  />
                </div>
                <div className="ml-auto flex items-center gap-vt-2">
                  <Button size="sm" variant="secondary" onClick={() => setPreallocDialogOpen(true)}>
                    Pre-allocate
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setBulkDialogOpen(true)}>
                    <Upload className="h-4 w-4" /> Bulk import
                  </Button>
                  <Button size="sm" onClick={() => openCreateSlot(slotZoneId || undefined)}>
                    <Plus className="h-4 w-4" /> Add slot
                  </Button>
                </div>
              </div>
              {slotsQuery.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : slotsQuery.isError ? (
                <EmptyState
                  icon={<ParkingCircle className="h-6 w-6" />}
                  title="Could not load slots"
                  description={getErrorMessage(slotsQuery.error)}
                />
              ) : slots.length === 0 ? (
                <EmptyState
                  icon={<ParkingCircle className="h-6 w-6" />}
                  title="No slots match these filters"
                  description="Adjust filters or add slots — bulk import is the fastest way."
                  action={
                    <Button onClick={() => setBulkDialogOpen(true)}>
                      <Upload className="h-4 w-4" /> Bulk import
                    </Button>
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Zone</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Ownership</TableHead>
                      <TableHead>Owner unit</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Monthly</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {slots.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-mono text-vt-13 font-medium text-vt-text">
                          {s.slot_number}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {s.zone?.name ?? '—'}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {SLOT_TYPE_LABEL[s.slot_type]}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {VEHICLE_SIZE_LABEL[s.vehicle_size]}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {OWNERSHIP_LABEL[s.ownership_kind]}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {s.owner_unit
                            ? `${s.owner_unit.block?.name ? s.owner_unit.block.name + ' · ' : ''}${s.owner_unit.number}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_BADGE[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr(s.monthly_fee_inr)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-vt-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Edit"
                              onClick={() => openEditSlot(s)}
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            {s.status === 'blocked' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Unblock"
                                onClick={() => unblockSlot.mutate(s.id)}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Block"
                                onClick={() => blockSlot.mutate(s.id)}
                              >
                                <Power className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Delete"
                              onClick={() => setDeleteSlotTarget(s)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <CursorPaginator
                hasMore={Boolean(slotPaginator.nextCursor)}
                loading={slotsQuery.isFetching}
                itemsShown={slots.length}
                pageSize={50}
                onLoadMore={() => {
                  if (slotPaginator.nextCursor) setSlotCursor(slotPaginator.nextCursor);
                }}
              />
            </CardContent>
          </Card>
        ) : null}

        {/* Allocations tab */}
        {tab === 'allocations' ? (
          <Card>
            <CardContent className="p-vt-4">
              <div className="mb-vt-3 flex flex-wrap items-center gap-vt-3">
                <TabsPills
                  value={allocActiveOnly}
                  onChange={(v) => setAllocActiveOnly(v as 'active' | 'history' | 'all')}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'history', label: 'History' },
                    { value: 'all', label: 'All' }
                  ]}
                  aria-label="Allocation status filter"
                />
                <Filter className="ml-vt-2 h-4 w-4 text-vt-text-muted" />
                <div className="flex-1 min-w-[180px]">
                  <Select
                    value={allocKindFilter}
                    onChange={(e) => setAllocKindFilter(e.target.value)}
                  >
                    <option value="">All kinds</option>
                    {(Object.keys(ALLOCATION_KIND_LABEL) as AllocationKind[]).map((k) => (
                      <option key={k} value={k}>
                        {ALLOCATION_KIND_LABEL[k]}
                      </option>
                    ))}
                  </Select>
                </div>
                <Button size="sm" className="ml-auto" onClick={() => setAllocDialogOpen(true)}>
                  <Plus className="h-4 w-4" /> New allocation
                </Button>
              </div>
              {allocsQuery.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : allocsQuery.isError ? (
                <EmptyState
                  icon={<CarFront className="h-6 w-6" />}
                  title="Could not load allocations"
                  description={getErrorMessage(allocsQuery.error)}
                />
              ) : allocations.length === 0 ? (
                <EmptyState
                  icon={<CarFront className="h-6 w-6" />}
                  title="No allocations"
                  description="Allocate available slots to units to start tracking parking."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Slot</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Kind</TableHead>
                      <TableHead className="text-right">Rent</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allocations.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell className="font-mono text-vt-13 font-medium text-vt-text">
                          {a.slot?.slot_number ?? '—'}
                          {a.slot?.zone?.name ? (
                            <span className="ml-vt-1 text-vt-12 text-vt-text-muted">
                              · {a.slot.zone.name}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {a.using_unit
                            ? `${a.using_unit.block?.name ? a.using_unit.block.name + ' · ' : ''}${a.using_unit.number}`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text">
                          {ALLOCATION_KIND_LABEL[a.allocation_kind]}
                          {a.is_primary_slot ? (
                            <Badge variant="info" className="ml-vt-1">
                              Primary
                            </Badge>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr(a.monthly_rent_inr)}
                        </TableCell>
                        <TableCell className="text-vt-12 text-vt-text-muted">
                          {a.rental_start
                            ? formatDate(a.rental_start)
                            : formatDate(a.allocated_at)}
                        </TableCell>
                        <TableCell>
                          {a.ended_at ? (
                            <Badge variant="muted">Ended</Badge>
                          ) : (
                            <Badge variant="ok">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {!a.ended_at ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="End allocation"
                              onClick={() => setEndAllocTarget(a)}
                            >
                              <Power className="h-4 w-4" /> End
                            </Button>
                          ) : (
                            <span className="text-vt-12 text-vt-text-muted">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <CursorPaginator
                hasMore={Boolean(allocPaginator.nextCursor)}
                loading={allocsQuery.isFetching}
                itemsShown={allocations.length}
                pageSize={50}
                onLoadMore={() => {
                  if (allocPaginator.nextCursor) setAllocCursor(allocPaginator.nextCursor);
                }}
              />
            </CardContent>
          </Card>
        ) : null}
      </div>

      {zoneDialog}
      {slotDialog}
      {bulkDialog}
      {preallocDialog}
      {allocDialog}
      {endAllocDialog}

      <ConfirmDialog
        open={Boolean(deleteZoneTarget)}
        onOpenChange={(v) => (v ? null : setDeleteZoneTarget(null))}
        title="Delete zone"
        description={
          deleteZoneTarget ? (
            <>
              Permanently delete <strong>{deleteZoneTarget.name}</strong>? Only allowed when the
              zone has no slots on file.
            </>
          ) : null
        }
        confirmLabel="Delete"
        variant="danger"
        typeToConfirm="DELETE"
        loading={deleteZone.isPending}
        onConfirm={() => {
          if (deleteZoneTarget) deleteZone.mutate(deleteZoneTarget.id);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteSlotTarget)}
        onOpenChange={(v) => (v ? null : setDeleteSlotTarget(null))}
        title="Delete slot"
        description={
          deleteSlotTarget ? (
            <>
              Permanently delete slot <strong>{deleteSlotTarget.slot_number}</strong>? Only
              allowed when the slot has no active allocation.
            </>
          ) : null
        }
        confirmLabel="Delete"
        variant="danger"
        loading={deleteSlot.isPending}
        onConfirm={() => {
          if (deleteSlotTarget) deleteSlot.mutate(deleteSlotTarget.id);
        }}
      />
    </>
  );
}

// ───── KPI card mini-component ─────

interface KpiCardProps {
  icon: typeof Layers;
  label: string;
  value: number | string;
  loading?: boolean;
  tone?: 'primary' | 'info' | 'safe' | 'warn' | 'muted';
  /** When true, render value as text without thousands formatting. */
  isText?: boolean;
}

function KpiCard({ icon: Icon, label, value, loading, tone = 'primary', isText = false }: KpiCardProps): JSX.Element {
  const toneClass: Record<NonNullable<KpiCardProps['tone']>, string> = {
    primary: 'bg-vt-primary-100 text-vt-primary',
    info: 'bg-[color:var(--vt-info-100)] text-vt-info',
    safe: 'bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]',
    warn: 'bg-[color:var(--vt-warn-100)] text-vt-warn',
    muted: 'bg-vt-surface-2 text-vt-text-muted'
  };
  return (
    <Card>
      <CardContent className="flex items-center gap-vt-3 p-vt-4">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md ${toneClass[tone]}`}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <div className="text-vt-12 text-vt-text-muted">{label}</div>
          <div className="text-vt-20 font-semibold text-vt-text">
            {loading ? (
              <Skeleton className="h-7 w-12" />
            ) : isText ? (
              <span>{value}</span>
            ) : (
              <span>{value}</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
